import { useState } from 'react';
import { BottomModal } from '../shared/BottomModal';
import { useEffect } from 'react';
import { apiLimits } from '../../api/apiLimits.api';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import config from '../../config';
import { apiMatch } from '../../api/apiMatch.api';

const limitsConfig = {
  like: {
    title: (current, total) => `Лайки закончились (${current}/${total})`,
    iconPatch: '/icons/photo_overlay_button_4.svg',
    buttonText: 'Хочу больше лайков в день'
  },
  superlike: {
    title: (current, total) => `Супер-лайки закончились (${current}/${total})`,
    iconPatch: '/icons/photo_overlay_button_3.svg',
    buttonText: 'Хочу 5 супер-лайков в день'
  },
  rewind: {
    title: (current, total) => `Возвраты закончились (${current}/${total})`,
    iconPatch: '/icons/photo_overlay_button_1.svg',
    buttonText: 'Хочу 5 возвратов в неделю'
  },
  partners: {
    title: (current, total) => `Просмотры закончились (${current}/${total})`,
    iconPatch: '/icons/ViewLimit.svg',
    buttonText: 'Хочу 600 просмотров в день'
  }
};

function formatTime(ms) {
  if (ms <= 0) return '00 ч 00 мин';

  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `${days} д ${String(hours % 24).padStart(2, '0')} ч`;
  }

  return `${String(hours).padStart(2, '0')} ч ${String(minutes).padStart(2, '0')} мин`;
}

export function LimitModal({ domain, limitsKey, setLimitsKey, fullScreen, onBuy }) {
  const axiosPrivate = useAxiosPrivate();

  const [limits, setLimits] = useState(null);
  const [plan, setPlan] = useState(null);
  const [cost, setCost] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isPaying, setIsPaying] = useState(false);

  const currentLimit =
    limits?.[limitsKey] && limitsConfig?.[limitsKey] ? { ...limitsConfig[limitsKey], ...limits[limitsKey] } : null;

  useEffect(() => {
    let interval;

    apiLimits.fetchMyLimits(axiosPrivate).then((res) => {
      setLimits(res.limits[domain]);
      setPlan(res.plan);

      // Суперлайк восстанавливается раз в 24 ч, возврат — раз в неделю:
      // для них таймер до конца окна, а не до конца дня
      const weeklyResetAt = {
        superlike: res.superLikesWeekResetAt,
        rewind: res.rewindsWeekResetAt,
      }[limitsKey];

      const weeklyReset = weeklyResetAt ? new Date(weeklyResetAt).getTime() : null;

      const updateTimeLeft = () => {
        const nextReset = weeklyReset ?? new Date(res.lastResetDate).getTime() + 24 * 60 * 60 * 1000;
        const left = nextReset - Date.now();

        setTimeLeft(left > 0 ? left : 0);
      };

      // показываем таймер сразу, не дожидаясь первого тика интервала
      updateTimeLeft();
      interval = setInterval(updateTimeLeft, 1000);
    });

    apiMatch.fetchLimitsCost(axiosPrivate).then(setCost);

    return () => clearInterval(interval);
  }, []);

  // Покупка действия за монеты прямо из модалки: списание с кошелька и
  // повтор действия. Если onBuy не передан — ведём на страницу пополнения.
  const handleBuy = async () => {
    if (!onBuy || isPaying) return;

    setIsPaying(true);
    try {
      await onBuy();
    } finally {
      setIsPaying(false);
    }
  };

  if (fullScreen) {
    return (
      <div className="bg-[#043939] relative flex-1 flex flex-col justify-center items-center w-full h-full z-50">
        {currentLimit && (
          <div className="flex flex-col items-center justify-center text-white">
            <img className="size-[80px] mb-4 text-center" src={currentLimit.iconPatch} alt="icon" />
            <p className="text-[22px] font-semibold mb-2 text-center">
              {currentLimit.title(currentLimit.current, currentLimit.limit)}
            </p>
            <p className="text-bas mb-5 text-center">Новые будут доступны через:</p>
            {timeLeft !== null && <p className="bg-[#0B6666] px-3 py-2 rounded-lg text-base">{formatTime(timeLeft)}</p>}
          </div>
        )}
        {plan === 'FREE' && (
          <Link
            className="bg-[#A1F69E] text-lg font-medium text-black rounded-full p-[18.5px] text-center mx-4 my-10"
            to={config.ROUTES.PREMIUM}
          >
            {currentLimit.buttonText}
          </Link>
        )}
      </div>
    );
  }

  return createPortal(
    <BottomModal isOpen={true} onClose={() => setLimitsKey(null)}>
      <div className="p-4">
        {currentLimit && (
          <div className="flex flex-col items-center justify-center text-white">
            <img className="size-[80px] mb-4 text-center" src={currentLimit.iconPatch} alt="icon" />
            <p className="text-[22px] font-semibold mb-2 text-center">
              {currentLimit.title(currentLimit.current, currentLimit.limit)}
            </p>
            <p className="text-bas mb-5 text-center">Новые будут доступны через:</p>
            {timeLeft !== null && <p className="bg-[#0B6666] px-3 py-2 rounded-lg text-base">{formatTime(timeLeft)}</p>}
            <div className='space-y-2 w-full mt-10'>
              {cost?.[limitsKey] &&
                (onBuy ? (
                  <button
                    type="button"
                    className="border-[#A1F69E] border text-lg font-medium text-[#A1F69E] rounded-full p-[18.5px] w-full flex items-center justify-center gap-2 disabled:opacity-50"
                    onClick={handleBuy}
                    disabled={isPaying}
                  >
                    {isPaying ? 'Оплата...' : 'Купить за'}
                    <img src="/icons/coin.svg" alt="монеты" className="size-[25px]" />
                    {cost[limitsKey]}
                  </button>
                ) : (
                  <Link
                    className="border-[#A1F69E] border text-lg font-medium text-[#A1F69E] rounded-full p-[18.5px] w-full flex items-center justify-center gap-2"
                    to={config.ROUTES.PAYMENT}
                  >
                    Купить за
                    <img src="/icons/coin.svg" alt="монеты" class="size-[25px]" />
                    {cost[limitsKey]}
                  </Link>
                ))}
              {plan === 'FREE' && (
                <Link
                  className="bg-[#A1F69E] text-lg font-medium text-black rounded-full p-[18.5px] w-full flex items-center justify-center"
                  to={config.ROUTES.PREMIUM}
                >
                  {currentLimit.buttonText}
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </BottomModal>,
    document.getElementById('portal')
  );
}
